import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './Dashboard.css';
import useRaceData from '../../hooks/useRaceData';

/**
 * İki nokta arasındaki mesafeyi deniz mili cinsinden hesaplar
 * @param {Array} point1 - [lat, lon] formatında ilk nokta
 * @param {Array} point2 - [lat, lon] formatında ikinci nokta
 * @returns {number} Mesafe (NM cinsinden)
 */
const calculateDistance = (point1, point2) => {
  const R = 6371; // Dünya yarıçapı (km)
  const lat1 = point1[0] * Math.PI / 180;
  const lat2 = point2[0] * Math.PI / 180;
  const deltaLat = (point2[0] - point1[0]) * Math.PI / 180;
  const deltaLon = (point2[1] - point1[1]) * Math.PI / 180;
  
  const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
          Math.cos(lat1) * Math.cos(lat2) *
          Math.sin(deltaLon/2) * Math.sin(deltaLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;
  
  // Kilometre -> Deniz mili dönüşümü (1 km = 0.539957 deniz mili)
  return distance * 0.539957;
};

/**
 * Dashboard component for displaying race metrics and boat information
 * Shows current speed, position, progress and comparison with record boat
 */
const Dashboard = () => {
  // API'den doğrudan veri almak için state'ler
  const [apiData, setApiData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Ortam kontrolü - Vercel'de çalışıyorsa veya HTTPS ise proxy kullan
  const isVercelEnvironment = typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');
  const isHttpsEnvironment = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const isProduction = isVercelEnvironment || isHttpsEnvironment;
  
  // Doğrudan API'den güncel veri al
  useEffect(() => {
    const getLatestData = async () => {
      try {
        setIsLoading(true);
        
        // API endpoint'i - üretim ortamında proxy kullan, geliştirme ortamında doğrudan API'ye bağlan
        const apiUrl = isProduction
          ? `/api/race?t=${Date.now()}`
          : `http://mavivatan.imarine.com.tr:3583/api/v1/race?t=${Date.now()}`;
        
        console.log(`Dashboard: API isteği yapılıyor: ${apiUrl}`);
        
        // API'den taze veri al
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            },
            cache: 'no-store'
          }
        );
        
        if (!response.ok) {
          throw new Error(`API isteği başarısız: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Dashboard: Doğrudan API yanıtı:', data);
        
        if (!data || !data.race || !data.tracks || !data.tracks.length) {
          throw new Error('API yanıtı geçerli veri içermiyor');
        }
        
        // API verisini state'e kaydet
        setApiData(data);
        setLastUpdate(new Date());
        setError(null);
      } catch (err) {
        console.error('Dashboard: API veri alma hatası:', err);
        setError('Veri alınamadı: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    // İlk yüklemede veri al
    getLatestData();
    
    // Her 15 saniyede bir güncelle
    const interval = setInterval(getLatestData, 15000);
    
    return () => clearInterval(interval);
  }, [isProduction]); // isProduction bağımlılığını ekledik
  
  // Ayrıca store'dan gelen veriyi de kullan (yedek olarak)
  const raceData = useRaceData();
  
  // Yarış başlangıç tarihi ve saati - useMemo içine taşındı
  
  // Format numbers with specified precision - güvenli hale getirildi
  const formatNumber = useCallback((number, precision = 1) => {
    // undefined, null veya sayı olmayan değerleri kontrol et
    if (number === undefined || number === null || isNaN(number)) {
      return '0';
    }
    return Number(number).toFixed(precision);
  }, []);
  
  // Toplam geçen süre hesaplama - useMemo ile optimize edildi
  const elapsedTime = useMemo(() => {
    // Yarış başlangıç tarihi ve saati
    const startDate = new Date(2025, 4, 22, 5, 46, 0); // 22 Mayıs 2025, 05:46
    const now = new Date();
    const elapsedMs = now - startDate;
    
    // Gün, saat, dakika hesaplama
    const days = Math.floor(elapsedMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((elapsedMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return { days, hours, minutes };
  }, []);  // Boş dependency array - startDate artık içeride tanımlandı
  
  // Loading state handling - doğrudan API verisini kontrol et
  if (isLoading && !apiData) {
    return (
      <div className="dashboard loading">
        <div className="data-panel">
          <h3>Veri Yükleniyor...</h3>
          <p>Lütfen bekleyin, yarış verileri getiriliyor.</p>
        </div>
      </div>
    );
  }
  
  // Error state handling - doğrudan API hatasını kontrol et
  if (error && !apiData) {
    return (
      <div className="dashboard error">
        <div className="data-panel error-panel">
          <h3>Bağlantı Hatası</h3>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Yeniden Dene</button>
        </div>
      </div>
    );
  }
  
  // API'den veri gelmedi ve store'dan da veri gelmedi
  if (!apiData && raceData.error) {
    return (
      <div className="dashboard error">
        <div className="data-panel error-panel">
          <h3>Veri Alınamadı</h3>
          <p>API'den ve store'dan veri alınamadı. Lütfen bağlantınızı kontrol edin.</p>
          <button onClick={() => window.location.reload()}>Yeniden Dene</button>
        </div>
      </div>
    );
  }

  // Current stage bilgisi
  // Not: recordBoat.stages[currentStage - 1] bilgisi gerekirse burada kullanılabilir

  // Doğrudan API'den gelen veriyi veya store'dan gelen veriyi kullan
  const currentData = apiData || (raceData.currentBoat ? { race: raceData.raceInfo, tracks: [raceData.currentBoat] } : null);
  
  // Veri yoksa yükleniyor göster
  if (!currentData) {
    return (
      <div className="dashboard loading">
        <div className="data-panel">
          <h3>Veri Yükleniyor...</h3>
          <p>Lütfen bekleyin, yarış verileri getiriliyor.</p>
        </div>
      </div>
    );
  }
  
  // API'den gelen güncel veriyi kullan - güvenli şekilde
  const track = currentData.tracks[0] || {};
  
  // Tüm değerleri güvenli şekilde al, undefined olma durumunu kontrol et
  const position = [
    track.lat !== undefined ? track.lat : 0,
    track.lon !== undefined ? track.lon : 0
  ];
  const currentSpeed = track.sog_kn !== undefined ? track.sog_kn : 0;
  const averageSpeed = track.asog_kn !== undefined ? track.asog_kn : 0;
  const course = track.cog_deg !== undefined ? track.cog_deg : 0;
  const raceTitle = currentData.race && currentData.race.title ? currentData.race.title : 'Yarış Bilgisi';
  
  // Son güncelleme zamanını göster
  const updateTimeStr = lastUpdate ? 
    `Son güncelleme: ${lastUpdate.toLocaleTimeString()}` : 
    'Veri güncelleniyor...';
  
  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Yarış Takip Paneli</h2>
        <div className="race-title">{raceTitle}</div>
        <div className="race-info">
          <div className="start-info">Start: 22 Mayıs 2025, TSI 05:46</div>
          <div className="elapsed-time">Geçen Süre: {elapsedTime.days} gün {elapsedTime.hours} saat {elapsedTime.minutes} dk</div>
          <div className="record-time">Mevcut Rekor: 10 gün 2 saat 44 dk</div>
        </div>
        <div className="update-time">{updateTimeStr}</div>
      </div>
      
      {/* Current position panel */}
      <div className="data-panel position-panel">
        <h3>
          <span className="icon">📍</span> Mevcut Konum
        </h3>
        <div className="data-row">
          <div className="data-col">
            <div className="data-label">Enlem:</div>
            <div className="data-value">{formatNumber(position[0], 4)}° N</div>
          </div>
          <div className="data-col">
            <div className="data-label">Boylam:</div>
            <div className="data-value">{formatNumber(position[1], 4)}° E</div>
          </div>
        </div>
        <div className="maps-link">
          <a 
            href={`https://www.google.com/maps?q=${position[0]},${position[1]}`} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <span className="icon">🗺️</span> Google Maps'te Görüntüle
          </a>
        </div>
      </div>
      
      {/* Speed panel */}
      <div className="data-panel speed-panel">
        <h3>
          <span className="icon">⚡</span> Hız Bilgileri
        </h3>
        <div className="data-row">
          <div className="data-col">
            <div className="data-label">Anlık Hız:</div>
            <div className="data-value highlight">{formatNumber(currentSpeed)} knot</div>
          </div>
          <div className="data-col">
            <div className="data-label">Ortalama Hız:</div>
            <div className="data-value">{formatNumber(averageSpeed)} knot</div>
          </div>
        </div>
        <div className="data-row">
          <div className="data-col">
            <div className="data-label">Rota (COG):</div>
            <div className="data-value">{course}°</div>
          </div>
        </div>
      </div>
      
      {/* Progress panel - Sadece ilerleme çubuğu */}
      <div className="data-panel progress-panel">
        <h3>
          <span className="icon">🏁</span> İlerleme Durumu
        </h3>
        <div className="progress-bar-container">
          {/* Doğru ilerleme hesaplaması */}
          {(() => {
            // Çanakkale Çıkışı koordinatları
            const canakkaleExit = [40.04150, 26.19118];
            
            // İlk 3 etabın toplam mesafesi (etap verilerinden)
            const etap1 = 295; // Hopa-Sinop
            const etap2 = 265; // Sinop-Boğaz Girişi
            const etap3 = 165; // Boğaz Girişi-Çanakkale Çıkışı
            
            // Tüm yarışın toplam mesafesi
            const totalRaceDistance = 1570; // Hopa-İskenderun toplam
            
            // Çanakkale Çıkışı'ndan teknenin konumuna olan mesafe
            const distanceFromCanakkale = calculateDistance(
              [track.lat, track.lon],
              canakkaleExit
            );
            
            // Toplam kat edilen mesafe = İlk 3 etap toplamı - Çanakkale'den mesafe
            const firstThreeStagesDistance = etap1 + etap2 + etap3;
            const coveredDistance = firstThreeStagesDistance - distanceFromCanakkale;
            
            // Doğru ilerleme yüzdesi
            const correctProgress = Math.min(100, (coveredDistance / totalRaceDistance) * 100);
            
            return (
              <>
                <div 
                  className="progress-bar" 
                  style={{ width: `${correctProgress}%` }}
                ></div>
                <div className="progress-text">{formatNumber(correctProgress)}%</div>
              </>
            );
          })()}
        </div>
      </div>
      
      {/* FB Doğuş karşılaştırma bölümü kaldırıldı */}
      
      {/* API Status */}
      <div className="api-status">
        <div className={`status-indicator ${raceData.apiStatus}`}></div>
        <div className="status-text">
          {raceData.apiStatus === 'connected' ? 'Bağlantı aktif' : 
           raceData.apiStatus === 'timeout' ? 'Bağlantı zaman aşımı' : 
           'Bağlantı hatası'}
        </div>
        <div className="last-update">Son güncelleme: {raceData.lastUpdate}</div>
      </div>
      
      {/* Legend */}
      <div className="legend">
        <div className="legend-item">
          <div className="legend-color current-boat"></div>
          <div className="legend-label">Linea Rossa</div>
        </div>
        <div className="legend-item">
          <div className="legend-color record-boat"></div>
          <div className="legend-label">Fenerbahçe Doğuş (Rekor Sahibi)</div>
        </div>
      </div>
      
      {/* Disclaimer note */}
      <div className="disclaimer-note">
        <p>
          <strong>Not:</strong> Bu simülasyon Fenerbahçe Doğuş Ekibinin rekor rotasındaki performansları kullanılarak ve Linea Rossa ile aynı anda start ettikleri varsayılarak hazırlanmıştır. Sadece genel bilgilendirme amaçlıdır. Kesin doğruluk beklenmemelidir.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
